import { deprecatedWarning, keys, toReversed } from '@rehaje/shared'

import { Abort } from './constants'
import { globalOrigins } from './lambdas'

import type { ObserveEvent, Reactive } from './types'
import type { Effect, TArray, TRecord, TStrongStruct } from '@rehaje/shared'

abstract class AbstractObserveEvent<T extends Reactive = Reactive> {
  private static __r_version = 0

  __r_effects?: Effect[]
  __r_initial_parents?: Reactive[]

  __r_initial_path?: any[]
  __r_observe_origin?: any[]
  __r_parents?: Reactive[]
  __r_path?: any[]
  __r_reverse_initial_parents?: Reactive[]
  __r_reverse_initial_path?: any[]
  __r_version = AbstractObserveEvent.__r_version++
  currentTarget: Reactive
  key?: any
  origins?: any[]
  abstract readonly source: any
  abstract readonly type: ObserveEvent['type']

  get initialParents(): Reactive[] {
    if (this.__r_initial_parents !== undefined) return this.__r_initial_parents
    this.__r_initial_parents = toReversed(this.__r_reverse_initial_parents)
    return this.__r_initial_parents
  }

  get initialPath(): any[] {
    if (this.__r_initial_path !== undefined) return this.__r_initial_path
    this.__r_initial_path = toReversed(this.__r_reverse_initial_path)
    return this.__r_initial_path
  }

  get origin(): any {
    return this.origins?.[0]
  }

  get parents(): Reactive[] {
    if (this.__r_parents !== undefined) return this.__r_parents
    if (this.__r_initial_parents === undefined) {
      this.__r_parents = toReversed(this.__r_reverse_initial_parents)
      if (this.key !== undefined) this.__r_parents.push(this.target)
    } else {
      this.__r_parents = this.__r_initial_parents
      if (this.key !== undefined)
        this.__r_parents = this.__r_parents.concat(this.target)
    }
    return this.__r_parents
  }

  set parents(parents: Reactive[]) {
    this.__r_parents = undefined
    this.__r_initial_parents = undefined
    this.__r_reverse_initial_parents = parents
  }

  get path(): any[] {
    if (this.__r_path !== undefined) return this.__r_path
    if (this.__r_initial_path === undefined) {
      this.__r_path = toReversed(this.__r_reverse_initial_path)
      if (this.key !== undefined) this.__r_path.push(this.key)
    } else {
      this.__r_path = this.__r_initial_path
      if (this.key !== undefined) this.__r_path = this.__r_path.concat(this.key)
    }
    return this.__r_path
  }

  set path(path: any[]) {
    this.__r_path = undefined
    this.__r_initial_path = undefined
    this.__r_reverse_initial_path = path
  }

  /**
   * @deprecated
   */
  get receiver(): T {
    deprecatedWarning('ObserveEvent.receiver')
    return this.target.__rr_deep ?? this.target.__rr_shallow ?? this.target
  }

  constructor(public target: T) {
    this.currentTarget = target
    if (globalOrigins.length > 0) this.origins = globalOrigins.slice()
  }

  __r_isOriginExecuted(origin?: any): boolean {
    if (origin === undefined) return false
    if (this.__r_observe_origin?.includes(origin)) return true
    this.__r_observe_origin ??= []
    this.__r_observe_origin.push(origin)
    return false
  }

  __r_runEffects() {
    if (this.__r_effects !== undefined) {
      for (const effect of this.__r_effects) effect()
      this.__r_effects = undefined
    }
  }

  abort(): never {
    throw Abort
  }

  addEffect(effect: Effect, priority?: 'high' | 'normal'): void {
    this.__r_effects ??= []
    if (priority === 'high') this.__r_effects.unshift(effect)
    else this.__r_effects.push(effect)
  }

  toJS(): this {
    const result: TRecord = {
      initialParents: this.initialParents.slice(),
      initialPath: this.initialPath.slice(),
      origin: this.origin,
      origins: this.origins?.slice(),
      parents: this.parents.slice(),
      path: this.path.slice()
    }
    for (const key of keys(this)) {
      if (key[0] === '_' || key in result) continue
      result[key] = this[key]
    }
    return result
  }

  toJSON(): this {
    return this.toJS()
  }
}

export class ObserveClearEvent extends AbstractObserveEvent<
  Reactive<TStrongStruct>
> {
  override readonly source = {}

  override readonly type = 'clear'
}

export class ObserveDeleteEvent extends AbstractObserveEvent {
  override readonly source: {
    key: any
  }

  override readonly type = 'delete'

  constructor(
    target: Reactive,
    public override key: any
  ) {
    super(target)
    this.source = {
      key
    }
  }
}

export class ObserveSetEvent extends AbstractObserveEvent {
  override readonly source: {
    key: any
    value: any
  }

  override readonly type = 'set'

  constructor(
    target: Reactive,
    public override key: any,
    public value: any
  ) {
    super(target)
    this.source = {
      key,
      value
    }
  }
}

export class ObserveSpliceEvent extends AbstractObserveEvent<Reactive<TArray>> {
  override readonly source: {
    deleteCount: number
    items: any[]
    start: number
  }

  override readonly type = 'splice'

  constructor(
    target: Reactive,
    public start: number,
    public deleteCount: number,
    public items: any[]
  ) {
    super(target as Reactive<TArray>)
    this.source = {
      deleteCount,
      items,
      start
    }
  }
}
